import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import React, { useMemo } from 'react'
import { useFarmContext } from 'src/Elements/Farm'
import usePortalQueries from 'src/Libs/api/hooks/use-portal-queries'
import { TimeInMilliseconds } from 'src/Libs/types'
import { useTypedSelector } from 'src/State/RootReducer'
import { fetchIsInstallationOnline } from '../api/installation.api'
import { InstallationOnlineStatusContext } from './installation-online-status-context'

interface Props {
  children?: React.ReactNode
}

/**
 * Provides information about online status for SSI and farms.
 * Online status of an installation depends on (1) the storage status api..
 */
const InstallationOnlineStatusProvider: React.FC<Props> = ({ children }) => {
  const storageId = useTypedSelector(state => state.storage.activeStorage)
  const authFetch = useAuthFetchWithResponse()
  const { isFarm, farmMaster, farmClients } = useFarmContext()
  const farmStorageIds = isFarm ? farmClients?.map(client => client.storageId).filter(Boolean) ?? [] : []
  const storageIdsToCheck = Array.from(new Set([...(storageId ? [storageId] : []), ...farmStorageIds]))

  const queries = usePortalQueries(
    storageIdsToCheck.map(id => ({
      queryKey: ['installation-online-status', id],
      queryFn: () => fetchIsInstallationOnline(id, authFetch),
      refetchInterval: TimeInMilliseconds.fifteenMinutes,
      refetchIntervalInBackground: true, // keeps refetching even when window is not focused
    }))
  )

  const value = useMemo(() => {
    const queriesWithStorageId = storageIdsToCheck.map((id, index) => ({
      storageId: id,
      query: queries[index],
    }))
    const loading = queriesWithStorageId.some(({ query }) => query.isLoading || query.isRefetching)
    if (loading)
      return {
        isServerOnline: true,
        isStorageOnline: true,
        offlineClientStorageIds: [],
        areAllClientsOnline: true,
        isLoading: true,
      }

    const isServerOnline = queriesWithStorageId.find(({ storageId }) => storageId === farmMaster?.storageId)?.query.data ?? true
    const isStorageOnline = queriesWithStorageId.find(({ storageId: id }) => id === storageId)?.query.data ?? true
    const isLoading = queries.some(query => query.isLoading)

    const offlineClientStorageIds: string[] = queriesWithStorageId.reduce((acc, { storageId: id, query }) => {
      if (query.data === false) {
        acc.push(id)
      }
      return acc
    }, [] as string[])

    return {
      isServerOnline,
      isStorageOnline,
      offlineClientStorageIds: offlineClientStorageIds,
      areAllClientsOnline: offlineClientStorageIds.length === 0,
      isLoading,
    }
  }, [storageIdsToCheck, queries, farmMaster?.storageId, storageId])

  return <InstallationOnlineStatusContext.Provider value={value}>{children}</InstallationOnlineStatusContext.Provider>
}

export default InstallationOnlineStatusProvider
